import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { batchesApi } from './batches.api';
import type {
  BatchDetail,
  BatchListPage,
  BatchPreviewResponse,
  BatchUploadResponse,
  UploadMetadata,
} from '@/types/api';

export const BATCHES_KEY = ['admin', 'batches'] as const;

export function useBatchesPage(limit = 25, offset = 0) {
  return useQuery<BatchListPage>({
    queryKey: [...BATCHES_KEY, 'page', limit, offset],
    queryFn: () => batchesApi.list(limit, offset),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useBatchDetail(id: string | undefined) {
  return useQuery<BatchDetail>({
    queryKey: [...BATCHES_KEY, 'detail', id],
    queryFn: () => batchesApi.detail(id!),
    enabled: !!id,
    staleTime: 10_000,
  });
}

export function usePreviewBatch() {
  return useMutation<BatchPreviewResponse, Error, File>({
    mutationFn: (file) => batchesApi.preview(file),
  });
}

export function useUploadBatch() {
  const qc = useQueryClient();
  return useMutation<BatchUploadResponse, Error, { file: File; metadata: UploadMetadata }>({
    mutationFn: ({ file, metadata }) => batchesApi.upload(file, metadata),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: BATCHES_KEY });
    },
  });
}
